import React, { useState, useEffect } from 'react';
import { 
  History, 
  Eye, 
  Calendar, 
  FileText, 
  Brain, 
  RefreshCw,
  Search
} from 'lucide-react';
import { ApiService } from '../services/api';

interface AnalysisRecord {
  id: number;
  createdAt: string;
  fileName: string;
  fileSize: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  content: string;
}

interface AnalysisStats {
  totalRecords: number;
  totalTokens: number;
  totalFileSize: number;
}

interface HistoryManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const HistoryManager: React.FC<HistoryManagerProps> = ({ isOpen, onClose }) => {
  const [records, setRecords] = useState<AnalysisRecord[]>([]);
  const [stats, setStats] = useState<AnalysisStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<AnalysisRecord | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [historyResponse, statsResponse] = await Promise.all([
        ApiService.getAnalysisHistory(),
        ApiService.getAnalysisStats()
      ]);

      if (historyResponse.success && historyResponse.data) {
        setRecords(historyResponse.data);
      }

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };


  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const filteredRecords = records.filter(record =>
    record.fileName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <History className="h-6 w-6 text-primary-500" />
            <span>Analysis History</span>
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <span className="text-xl">×</span>
            </button>
          </div>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Left Panel - Records List */}
          <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 flex flex-col">
            {/* Stats */}
            {stats && (
              <div className="p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                      {stats.totalRecords}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Records</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {stats.totalTokens.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Tokens</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {formatFileSize(stats.totalFileSize)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Size</div>
                  </div>
                </div>
              </div>
            )}

            {/* Search */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-600">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by filename..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Records List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : filteredRecords.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400">
                  <FileText className="h-8 w-8 mb-2" />
                  <p>No analysis records found</p>
                </div>
              ) : (
                <div className="space-y-2 p-4">
                  {filteredRecords.map((record) => (
                    <div
                      key={record.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedRecord?.id === record.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                      onClick={() => setSelectedRecord(record)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {record.fileName}
                          </h3>
                          <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>{formatDate(record.createdAt)}</span>
                            </span>
                            <span>{formatFileSize(record.fileSize)}</span>
                            <span>{record.totalTokens} tokens</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Record Details */}
          <div className="w-1/2 flex flex-col">
            {selectedRecord ? (
              <>
                <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <Brain className="h-5 w-5 text-primary-500" />
                    <span>Analysis Details</span>
                  </h3>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-4">
                    {/* File Info */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">File Information</h4>
                      <div className="space-y-1 text-sm">
                        <div><strong>Name:</strong> {selectedRecord.fileName}</div>
                        <div><strong>Size:</strong> {formatFileSize(selectedRecord.fileSize)}</div>
                        <div><strong>Analyzed:</strong> {formatDate(selectedRecord.createdAt)}</div>
                      </div>
                    </div>

                    {/* Token Usage */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">Token Usage</h4>
                      <div className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
                        <div><strong>Total:</strong> {selectedRecord.totalTokens.toLocaleString()} tokens</div>
                        <div><strong>Prompt:</strong> {selectedRecord.promptTokens.toLocaleString()} tokens</div>
                        <div><strong>Completion:</strong> {selectedRecord.completionTokens.toLocaleString()} tokens</div>
                      </div>
                    </div>

                    {/* Analysis Content */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Analysis Content</h4>
                      <div className="max-h-64 overflow-y-auto">
                        <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-sans">
                          {selectedRecord.content}
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                <div className="text-center">
                  <Eye className="h-12 w-12 mx-auto mb-4" />
                  <p>Select a record to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default HistoryManager;
